import { DefaultContainer } from '@/components/DefaultContainer';
import { useExerciseCategoriesQuery } from '@/hooks/query-hooks';
import { Box, Grid, IconButton, Link, List, ListItem, ListItemButton, ListSubheader, Paper, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { UpdateExerciseCategoryModal } from './UpdateExerciseCategoryModal';
import { useState } from 'react';
import { CreateExerciseCategoryDTO, ExerciseCategoryDTO } from '@/generated';
import { ImageFromServer } from '@/components/images/ImageFromServer';
import { DeleteModal } from '@/components/DeleteModal';
import { useDeleteExerciseCategory } from '@/hooks/category-mutation.hooks';
import { enqueueSnackbar } from 'notistack';

const listPadding = 1;
const listGap = 0.5;

export const ExerciseCategories = () => {
  const categories = useExerciseCategoriesQuery(true);
  const [category, setCategory] = useState<ExerciseCategoryDTO | CreateExerciseCategoryDTO>();
  const [categoryToDeleteId, setCategoryToDeleteId] = useState<string>();
  const deleteCategory = useDeleteExerciseCategory();

  return (
    <DefaultContainer>
      <UpdateExerciseCategoryModal setCategory={setCategory} category={category} />
      <DeleteModal
        nameOfObjectToDelete="Category"
        handleDelete={async () => {
          if (categoryToDeleteId) await deleteCategory.mutateAsync(categoryToDeleteId);
          setCategoryToDeleteId(undefined);
          enqueueSnackbar(`Category deleted!`);
        }}
        setShow={(show) => {
          if (!show) setCategoryToDeleteId(undefined);
        }}
        show={categoryToDeleteId !== undefined}
      />
      <Paper sx={{ my: 1, zIndex: 1, padding: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
        <Grid sx={{ alignContent: 'center', alignItems: 'center' }} container gap={2} direction={'row'}>
          <Grid>
            <Tooltip title={'Create New Category'}>
              <IconButton
                onClick={() => {
                  setCategory({ description: '', name: '', links: [] });
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>
      <Grid size={{ xs: 12, md: 3 }} spacing={1} sx={{ my: 1 }} container>
        {categories.data?.map((c) => (
          <Grid
            sx={{ cursor: 'pointer' }}
            onClick={(e) => {
              setCategory(c);
              e.stopPropagation();
            }}
            size={{ xs: 12, md: 12 }}
          >
            <Stack sx={{ justifyContent: 'flex-start', gap: listGap, mx: 2 }} direction={'row'}>
              <Paper sx={{ minWidth: 50, p: listPadding, alignContent: 'center', justifyItems: 'center' }} elevation={4}>
                <ImageFromServer sx={{ width: 200, height: 200 }} id={c.imageId} thumbnail={true} alt={''} />
                <Typography sx={{ mt: 1 }}>{c.name}</Typography>
              </Paper>

              <Paper sx={{ p: listPadding, alignContent: 'center', flex: 0.55, flexGrow: 1 }} elevation={4}>
                {c.description}
                <CategoryLinks category={c} />
              </Paper>
              <Paper sx={{ p: listPadding, alignContent: 'flex-start', flex: 0.25 }} elevation={4}>
                <List sx={{ backgroundColor: 'background.paper' }}>
                  <ListSubheader>Actions</ListSubheader>
                  <ListItem>
                    <ListItemButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setCategoryToDeleteId(c.id);
                      }}
                    >
                      Delete
                    </ListItemButton>
                  </ListItem>
                </List>
              </Paper>
            </Stack>
          </Grid>
        ))}
      </Grid>
    </DefaultContainer>
  );
};

const CategoryLinks = (props: { category: ExerciseCategoryDTO }) => {
  const { category } = props;
  return category.links.length ? (
    <Stack sx={{ mt: 1 }}>
      <Typography>Links:</Typography>
      {props.category.links.map((el) => (
        <Box>
          <Link onClick={(e) => e.stopPropagation()} href={el.uri}>
            {el.title}
          </Link>
        </Box>
      ))}
    </Stack>
  ) : (
    <></>
  );
};
